// @ts-nocheck
import { useEffect, useRef, useState, Ref, useMemo, useCallback } from "react";
import { useNavigationStore } from "src/store/navigationStore";

import { Canvas, useFrame, RootState } from "@react-three/fiber";
import { Splat, useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { Group } from "three";

import { GLB_AVATAR } from "src/const/navigationConst";

import { useModelViewerStore } from "@/store/modelViewerStore";

import {
  roundToDecimals,
  vec3Distance,
  hexToRgbArray,
  getYAngleDegree,
  angleDifferenceABtoAC,
  wrapAngle,
  calculateTargetPosition,
  normalizeRotation,
} from "src/utils/game";
import * as pc from "playcanvas";
import { OrbitControls } from "@playcanvas/react/scripts";
import {
  useTexture,
  useMaterial,
  useEnvAtlas,
  useApp,
  useSplat,
  useModel,
  useAppEvent,
} from "@playcanvas/react/hooks";
import { Application, Entity, Gltf } from "@playcanvas/react";
import {
  Render,
  Camera,
  Light,
  GSplat,
  Script,
  Environment,
} from "@playcanvas/react/components";
import { CameraControls } from "playcanvas/scripts/esm/camera-controls.mjs";

import WatermarkScreen from "src/components/watermark";
import { useLodSplat } from "src/utils/useLodSplat";
import LottieLoading from "src/components/lottie/lottie-loading";

import { GsplatRevealRadial } from "src/extra_js/reveal-radial.mjs";

import {
  createRadialScript,
  SplatSogComponent,
  installCameraNearFar,
} from "@/components/playcanvas/playcanvasComponents";

const SAMPLE_MODEL =
  "https://assets.deepworldmodel.ai/venue/xiqu/modelViewer/xiqu_20260625_2320.sog";

export default function ModelViewer() {
  const interruptAutoPlay = useModelViewerStore(
    (state) => state.interruptAutoPlay,
  );
  const setProgress = useModelViewerStore((state) => state.setProgress);
  const setIsModelLoaded = useModelViewerStore(
    (state) => state.setIsModelLoaded,
  );
  const setErrorMessage = useModelViewerStore((state) => state.setErrorMessage);

  // const setIsAutoPlay = useModelViewerStore((state) => state.setIsAutoPlay);
  const startAutoPlayDelay = useModelViewerStore(
    (state) => state.startAutoPlayDelay,
  );
  const stopAutoPlay = useModelViewerStore((state) => state.stopAutoPlay);

  const moveSpeedRef = useRef<number>(2);
  const rotateSpeedRef = useRef<number>(20);

  // const defaultPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
  // const defaultTargetRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, -1));

  const targetAltitudeRef = useRef<number>(0);

  const latestCurrentLevelNameRef = useRef<string>("");

  const mainObjectEntityRef = useRef();
  const cameraScriptRef = useRef();
  const firstPersonCameraEntityRef = useRef();
  const thirdPersonCameraEntityRef = useRef();
  const latestCameraModeRef = useRef<number>(0);

  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const isMovingRef = useRef<boolean>(false);
  const isRotatingRef = useRef<boolean>(false);

  const latestPositionJoystickData = useRef<number[]>([0, 0]);
  const latestRotationJoystickData = useRef<number[]>([0, 0]);
  const latestRotationScreenData = useRef<number[]>([0, 0]);

  const latestResetPositionRotationEvent = useRef<number>(0);
  const latestResetOnlyRotationEvent = useRef<number>(0);

  const latestVenueModelConfigRef = useRef<VenueModelConfig | null>(null);

  const isInstantUpdate = useRef<boolean>(false);

  const latestModelJsonData = useRef<any>(null);

  const hiddenNearRef = useRef<pc.Entity>(null);
  const hiddenFarRef = useRef<pc.Entity>(null);

  const cameraArrayPosition = useRef([]);
  const cameraArrayTarget = useRef([]);

  const latestProgressRef = useRef(0);

  const AUTOPLAY_SPEED = 6;
  const autoPlaySpeedRef = useRef<number>(AUTOPLAY_SPEED);
  const isAutoPlayingRef = useRef<boolean>(false);
  const autoPlayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const eulerRef = useRef(new pc.Vec3(0, 0, 0));

  function stopAutoPlayFunction() {
    if (autoPlayTimeoutRef.current) {
      clearTimeout(autoPlayTimeoutRef.current);
    }
    autoPlayTimeoutRef.current = null;
    isAutoPlayingRef.current = false;
  }
  function resumeAutoPlay(milliseconds: number) {
    if (milliseconds > 0) {
      stopAutoPlayFunction();
    }
    autoPlayTimeoutRef.current = setTimeout(() => {
      autoPlayTimeoutRef.current = null;
      isAutoPlayingRef.current = true;
    }, milliseconds);
  }

  function interpolateVector3Array(points, percentage) {
    if (percentage <= 0) return points[0];
    if (percentage >= 1) return points[points.length - 1];

    const segments = points.length - 1;
    const scaledPosition = percentage * segments;
    const segmentIndex = Math.floor(scaledPosition);
    const segmentFraction = scaledPosition - segmentIndex;

    const start = points[segmentIndex];
    const end = points[segmentIndex + 1];

    return [
      start[0] + (end[0] - start[0]) * segmentFraction,
      start[1] + (end[1] - start[1]) * segmentFraction,
      start[2] + (end[2] - start[2]) * segmentFraction,
    ];
  }

  useEffect(() => {
    setIsModelLoaded(false);
    setErrorMessage("");
  }, []);

  function useQueryParams() {
    return useMemo(() => new URLSearchParams(window.location.search), []);
  }

  const DataController = () => {
    const resetPositionRotationEvent = useModelViewerStore(
      (state) => state.resetPositionRotationEvent,
    );
    useEffect(() => {
      latestResetPositionRotationEvent.current = resetPositionRotationEvent;
    }, [resetPositionRotationEvent]);

    const resetOnlyRotationEvent = useModelViewerStore(
      (state) => state.resetOnlyRotationEvent,
    );
    useEffect(() => {
      latestResetOnlyRotationEvent.current = resetOnlyRotationEvent;
    }, [resetOnlyRotationEvent]);

    const positionJoystickData = useModelViewerStore(
      (state) => state.positionJoystickData,
    );
    const rotationJoystickData = useModelViewerStore(
      (state) => state.rotationJoystickData,
    );

    useEffect(() => {
      latestPositionJoystickData.current = [
        positionJoystickData[0],
        positionJoystickData[1],
      ];
    }, [positionJoystickData]);

    useEffect(() => {
      latestRotationJoystickData.current = [
        rotationJoystickData[0],
        rotationJoystickData[1],
      ];
    }, [rotationJoystickData]);

    const progress = useModelViewerStore((state) => state.progress);

    useEffect(() => {
      latestProgressRef.current = progress;
      if (
        cameraArrayPosition.current.length <= 0 ||
        cameraArrayTarget.current.length <= 0
      ) {
        return;
      }
      const newPosition = interpolateVector3Array(
        cameraArrayPosition.current,
        progress,
      );
      const newRotation = interpolateVector3Array(
        cameraArrayTarget.current,
        progress,
      );
      if (newPosition && newRotation) {
        setPositionTarget(
          newPosition[0],
          newPosition[1],
          newPosition[2],
          newRotation[0],
          newRotation[1],
          newRotation[2],
        );
      }

      // if (mainObjectEntityRef.current) {
      //   mainObjectEntityRef.current.setPosition(
      //     newPosition[0],
      //     newPosition[1],
      //     newPosition[2],
      //   );
      //   mainObjectEntityRef.current.setLocalEulerAngles(
      //     newRotation[0],
      //     newRotation[1],
      //     newRotation[2],
      //   );
      //   isMovingRef.current = true;
      //   isRotatingRef.current = true;
      // }

      // eulerRef.current.x = newRotation[0];
      // eulerRef.current.y = newRotation[1];
      // eulerRef.current.z = newRotation[2];

      //updateCameraData();
      // setCameraAngle(newRotation[1]);
    }, [progress]);

    const startAutoPlayData = useModelViewerStore(
      (state) => state.startAutoPlayData,
    );
    useEffect(() => {
      if (startAutoPlayData.time > 0) {
        // setProgress(0);
        resumeAutoPlay(startAutoPlayData.delay);
      }
    }, [startAutoPlayData]);

    const stopAutoPlayData = useModelViewerStore(
      (state) => state.stopAutoPlayData,
    );
    useEffect(() => {
      stopAutoPlayFunction();
    }, [stopAutoPlayData]);

    return <></>;
  };

  const MainCamera = () => {
    const app = useApp();
    const cameraRef = useRef();
    let _localProgress;
    let _localCameraMode;
    let isCameraModeUpdated;
    let nearestFixedCameraPosition;

    let finalCameraPosition;
    let finalCameraLookingPosition;

    app.on("update", function (dt) {
      if (cameraScriptRef.current == null) {
        return;
      }

      isCameraModeUpdated = _localCameraMode != latestCameraModeRef.current;
      _localCameraMode = latestCameraModeRef.current;

      if (
        isInstantUpdate.current ||
        isCameraModeUpdated ||
        isMovingRef.current ||
        isRotatingRef.current
      ) {
        const newPosition =
          latestCameraModeRef.current == 0
            ? firstPersonCameraEntityRef.current.getPosition()
            : thirdPersonCameraEntityRef.current.getPosition();
        cameraScriptRef.current.reset(
          mainObjectEntityRef.current.getPosition(),
          newPosition,
        );

        isMovingRef.current = false;
        isRotatingRef.current = false;
        isInstantUpdate.current = false;
      }
    });

    useEffect(() => {
      const defaultCameraLookingPosition = new pc.Vec3(0, 0, 0);
      const defaultPosition = new pc.Vec3(0, 0, 0);
      cameraScriptRef.current.reset(
        defaultCameraLookingPosition,
        defaultPosition,
      );
      cameraScriptRef.current._camera.gammaCorrection = 1;
      cameraScriptRef.current._camera.toneMapping = 0;
      //cameraScriptRef.current._camera.toneMapping = pc.TONEMAP_ACES2;
      cameraScriptRef.current._camera.jitter = 0;
      cameraScriptRef.current._camera.shutter = 0;
    }, []);

    app.on("update", function (dt) {
      if (cameraScriptRef.current == null) {
        return;
      }
    });

    return (
      <Entity name="MainCamera">
        <Camera fov={75} clearColor="#000000" clearColorBuffer={true} />
        <Script
          ref={cameraScriptRef}
          script={CameraControls}
          enableFly={false}
        />
        <Entity ref={hiddenNearRef} position={[0, 0, 0]}></Entity>
        <Entity ref={hiddenFarRef} position={[0, 0, -0.5]}></Entity>
      </Entity>
    );
  };

  const setPositionTarget = (
    positionX: number,
    positionY: number,
    positionZ: number,
    targetX: number,
    targetY: number,
    targetZ: number,
  ) => {
    const newPosition = new pc.Vec3(-positionX, positionY, -positionZ);
    const newTarget = new pc.Vec3(-targetX, targetY, -targetZ);
    if (mainObjectEntityRef.current) {
      mainObjectEntityRef.current.setPosition(newPosition);
      mainObjectEntityRef.current.lookAt(newTarget);
      const initAngles = mainObjectEntityRef.current.getEulerAngles();
      if (initAngles.z < 90 && initAngles.z > -90) {
        eulerRef.current.set(initAngles.x, initAngles.y, 0);
      } else if (initAngles.z > 90 || initAngles.z < -90) {
        if (initAngles.x > 0) {
          eulerRef.current.set(initAngles.x - 180, 180 - initAngles.y, 0);
        } else {
          eulerRef.current.set(initAngles.x + 180, 180 - initAngles.y, 0);
        }
      } else {
        eulerRef.current.set(0, 0, 0);
      }
      isInstantUpdate.current = true;
    }
  };

  const MainObject = () => {
    const queryParams = useQueryParams();
    let modelLink = queryParams.get("model");

    modelLink = modelLink == null ? SAMPLE_MODEL : modelLink;

    let modelFormat = null;
    let modelJsonLink = null;
    if (modelLink != null) {
      if (modelLink.includes(".sog")) {
        modelFormat = "sog";
      } else if (modelLink.includes(".ply")) {
        modelFormat = "ply";
      }
    }

    const app = useApp();

    const defaultPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
    const defaultTargetRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, -1));

    let _localResetPositionRotationEvent = 0;
    let _localResetOnlyRotationEvent = 0;
    let _localLeaveVenueEvent = 0;

    useEffect(() => {
      if (latestModelJsonData.current == null) {
        if (modelFormat == "ply") {
          modelJsonLink = modelLink.replace(".ply", ".json");
        } else if (modelFormat == "sog") {
          modelJsonLink = modelLink.replace(".sog", ".json");
        } else {
          return;
        }
        fetchModelJsonData(modelJsonLink);
      } else {
        if (
          latestModelJsonData.current.position &&
          latestModelJsonData.current.target
        ) {
          setPositionTarget(
            latestModelJsonData.current.position.x,
            latestModelJsonData.current.position.y,
            latestModelJsonData.current.position.z,
            latestModelJsonData.current.target.x,
            latestModelJsonData.current.target.y,
            latestModelJsonData.current.target.z,
          );
        }
      }
    }, []);

    const fetchModelJsonData = async (link: string) => {
      try {
        const response = await fetch(link);
        if (!response.ok) throw new Error("Model Json Fetch failed");
        const jsonData = await response.json();
        latestModelJsonData.current = jsonData;
        if (jsonData.position && jsonData.target) {
          setPositionTarget(
            jsonData.position.x,
            jsonData.position.y,
            jsonData.position.z,
            jsonData.target.x,
            jsonData.target.y,
            jsonData.target.z,
          );
        } else {
          setPositionTarget(0, 0, 0, 0, 0, 1);
        }
        if (jsonData.cameras) {
          const newPositionArray = jsonData.cameras.map((cam) => [
            cam.position.x,
            cam.position.y,
            cam.position.z,
          ]);
          const newTargetArray = jsonData.cameras.map((cam) => [
            cam.target.x,
            cam.target.y,
            cam.target.z,
          ]);
          cameraArrayPosition.current = newPositionArray;
          cameraArrayTarget.current = newTargetArray;
        }
      } catch (err) {
        setPositionTarget(0, 0, 0, 0, 0, 1);
      } finally {
      }
    };

    // const setInitPositionAngle = (
    //   tx: number,
    //   ty: number,
    //   tz: number,
    //   qx: number,
    //   qy: number,
    //   qz: number,
    //   qw: number,
    // ) => {
    //   defaultPositionRef.current.set(-tx, -ty, -tz);
    //   const target = calculateTargetPosition(-tx, -ty, -tz, -qx, qy, qz, qw);
    //   defaultTargetRef.current.set(target.x, target.y, target.z);
    //   mainObjectEntityRef.current.setPosition(defaultPositionRef.current);
    //   mainObjectEntityRef.current.lookAt(defaultTargetRef.current);
    //   const initAngles = mainObjectEntityRef.current.getEulerAngles();

    //   if (initAngles.z < 90 && initAngles.z > -90) {
    //     console.log("type A");
    //     eulerRef.current.set(initAngles.x, initAngles.y, 0);
    //   } else if (initAngles.z > 90 || initAngles.z < -90) {
    //     console.log("type B");
    //     eulerRef.current.set(initAngles.x - 180, 180 - initAngles.y, 0);
    //   } else {
    //     console.log("type C");
    //     eulerRef.current.set(0, 0, 0);
    //   }
    //   isInstantUpdate.current = true;
    // };

    app.on("update", function (dt) {
      if (mainObjectEntityRef.current == null) {
        return;
      }
      let isResetPositionRotation =
        _localResetPositionRotationEvent !=
        latestResetPositionRotationEvent.current;
      _localResetPositionRotationEvent =
        latestResetPositionRotationEvent.current;

      let isResetOnlyRotation =
        _localResetOnlyRotationEvent != latestResetOnlyRotationEvent.current;
      _localResetOnlyRotationEvent = latestResetOnlyRotationEvent.current;

      // if (latestPositionJoystickData.current.length != 2) {
      //   return;
      // }

      // if (
      //   latestPositionJoystickData.current[0] != 0 ||
      //   latestPositionJoystickData.current[1] != 0
      // ) {
      //   //stopAutoPlay();
      //   isMovingRef.current = true;
      //   const worldMat = mainObjectEntityRef.current.getWorldTransform();
      //   const forward = new pc.Vec3(0, 0, -1);
      //   worldMat.transformVector(forward, forward);
      //   // forward.y = 0;
      //   forward.normalize();
      //   const up = pc.Vec3.UP.clone();
      //   const right = new pc.Vec3();
      //   right.cross(up, forward).normalize();
      //   const forwardScale =
      //     latestPositionJoystickData.current[1] * moveSpeedRef.current * -dt;
      //   const rightScale =
      //     latestPositionJoystickData.current[0] * moveSpeedRef.current * -dt;
      //   const forwardMove = forward.clone().mulScalar(forwardScale);
      //   const rightMove = right.clone().mulScalar(rightScale);
      //   const move = new pc.Vec3();
      //   move.add(forwardMove).add(rightMove);
      //   mainObjectEntityRef.current.translate(move);
      // }

      // if (
      //   latestRotationJoystickData.current[0] != 0 ||
      //   latestRotationJoystickData.current[1] != 0
      // ) {
      //   //stopAutoPlay();
      //   isRotatingRef.current = true;

      //   eulerRef.current.y +=
      //     -latestRotationJoystickData.current[0] * rotateSpeedRef.current * dt; // Yaw
      //   eulerRef.current.x +=
      //     -latestRotationJoystickData.current[1] * rotateSpeedRef.current * dt; // Pitch

      //   eulerRef.current.x = pc.math.clamp(eulerRef.current.x, -45, 45);
      //   eulerRef.current.y = wrapAngle(eulerRef.current.y);
      //   mainObjectEntityRef.current.setLocalEulerAngles(eulerRef.current);
      // }

      if (
        latestRotationScreenData.current[0] != 0 ||
        latestRotationScreenData.current[1] != 0
      ) {
        stopAutoPlay();
        isRotatingRef.current = true;
        eulerRef.current.y +=
          -latestRotationScreenData.current[0] * rotateSpeedRef.current * dt; // Yaw
        eulerRef.current.x +=
          -latestRotationScreenData.current[1] * rotateSpeedRef.current * dt; // Pitch

        eulerRef.current.x = pc.math.clamp(eulerRef.current.x, -45, 45);
        eulerRef.current.y = wrapAngle(eulerRef.current.y);

        mainObjectEntityRef.current.setLocalEulerAngles(eulerRef.current);
      }
    });

    return (
      <Entity ref={mainObjectEntityRef}>
        {/*<Avatar />*/}
        <Entity
          ref={firstPersonCameraEntityRef}
          position={[0, 0, 0.001]}
        ></Entity>
        <Entity
          ref={thirdPersonCameraEntityRef}
          position={[0, 0.5, 4]}
        ></Entity>
      </Entity>
    );
  };

  const cleanup = (app, gsplatEntity) => {
    const cleanup = installCameraNearFar(
      app,
      gsplatEntity,
      hiddenNearRef.current,
      hiddenFarRef.current,
    );
    return cleanup;
  };

  const ModelSplat = () => {
    const app = useApp();
    const queryParams = useQueryParams();
    let modelLink = queryParams.get("model");
    modelLink = modelLink == null ? SAMPLE_MODEL : modelLink;
    // const pathJsonLink = modelLink.replace(".sog", "_customPath.json");
    const callbackReadyFunction = (splat) => {
      console.log("single sog ready");
      setTimeout(() => {
        splat.gsplat.enabled = true;
        createRadialScript(mainObjectEntityRef.current, splat, true);
        setIsModelLoaded(true);
      }, 200);
      setTimeout(() => {
        //cleanup(app, splat);

        //resumeAutoPlay(0)
        if (!isAutoPlayingRef.current) {
          startAutoPlayDelay(0);
        }
      }, 4000);
    };

    return (
      <SplatSogComponent
        app={app}
        url={modelLink}
        position={[0, 0, 0]}
        rotation={[180, 0, 0]}
        scale={[1, 1, 1]}
        callbackReady={callbackReadyFunction}
      />
    );
  };

  const PlaycanvasScene = () => {
    const app = useApp();
    const newProgress = useRef(0);
    app.graphicsDevice.maxPixelRatio = 2;
    const handleUpdate = useCallback((dt: number) => {
      if (isAutoPlayingRef.current) {
        if (
          cameraArrayPosition.current.length == 0 ||
          cameraArrayTarget.current.length == 0
        ) {
          return;
        }
        newProgress.current =
          latestProgressRef.current +
          (autoPlaySpeedRef.current / cameraArrayPosition.current.length) * dt;
        newProgress.current = newProgress.current > 1 ? 1 : newProgress.current;
        setProgress(newProgress.current);
        if (newProgress.current == 1) {
          stopAutoPlay();
        }
      }
    }, []);
    useAppEvent("update", handleUpdate);
    return (
      <>
        {/*<ModelPly />*/}
        <ModelSplat />
        <MainObject />
        <MainCamera />
      </>
    );
  };

  const Scene = () => {
    return (
      <Application
        usePhysics={false}
        maxDeltaTime={0.033}
        graphicsDeviceOptions={{
          alpha: true,
          antialias: false,
          depth: true,
          stencil: false,
          powerPreference: "high-performance",
        }}
      >
        <PlaycanvasScene />
      </Application>
    );
  };

  const Loading = () => {
    const isModelLoaded = useModelViewerStore((state) => state.isModelLoaded);
    return !isModelLoaded ? (
      <div className="frosted-fullscreen flex flex-col items-center justify-center">
        <LottieLoading />
      </div>
    ) : (
      <></>
    );
  };

  const ErrorMessage = () => {
    const errorMessage = useModelViewerStore((state) => state.errorMessage);
    return errorMessage != "" ? (
      <div className="frosted-fullscreen flex flex-col items-center justify-center">
        {errorMessage}
      </div>
    ) : (
      <></>
    );
  };

  console.log(
    "Model Viewer Page component rendered , if this keep showing, something wrong",
  );

  const DragController = () => {
    const isDraggingRef = useRef(false);
    const startPosRef = useRef({ x: 0, y: 0 });
    const previousPositionRef = useRef({ x: 0, y: 0 });
    const getClientPos = (e) => {
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    };
    const handleStart = useCallback((e) => {
      const pos = getClientPos(e);
      isDraggingRef.current = true;
      previousPositionRef.current = { x: pos.x, y: pos.y };
      //console.log("stop");
      //startAutoPlayDelay(2000);
    }, []);
    const handleMove = useCallback((e) => {
      if (!isDraggingRef.current) {
        return;
      }
      const pos = getClientPos(e);
      const xChange = pos.x - previousPositionRef.current.x;
      const yChange = pos.y - previousPositionRef.current.y;
      previousPositionRef.current = { x: pos.x, y: pos.y };
      latestRotationScreenData.current[0] = xChange * 0.5;
      latestRotationScreenData.current[1] = yChange * 0.5;
    }, []);
    const handleEnd = useCallback(() => {
      isDraggingRef.current = false;
      latestRotationScreenData.current[0] = 0;
      latestRotationScreenData.current[1] = 0;
      startAutoPlayDelay(2000);
    }, []);
    useEffect(() => {
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchend", handleEnd);
      return () => {
        window.removeEventListener("mouseup", handleEnd);
        window.removeEventListener("touchend", handleEnd);
      };
    }, []);

    return (
      <div
        className="fullscreen_div"
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        onMouseMove={handleMove}
        onTouchMove={handleMove}
        onMouseUp={handleEnd}
        onTouchEnd={handleEnd}
      ></div>
    );
  };

  return (
    <div
      className="absolute top-0 left-0 w-full h-full"
      style={{ backgroundColor: "black" }}
    >
      <Loading />
      <ErrorMessage />
      <DataController />
      <Scene />
      <DragController />
      <WatermarkScreen />
    </div>
  );
}
